<template>
	<div class="crdRoot">
		<div class="pageHeading">Code Review Diagnostics</div>
		<div class="warningBox">Temporary diagnostics for code-review jobs on a release agent. Admin only.</div>

		<!-- Agent -->
		<div class="card">
			<div class="secondaryHeading">Agent</div>
			<div class="row">
				<label for="crdAgent">Agent</label>
				<select id="crdAgent" v-model="agentName" :disabled="agentsLoading">
					<option v-for="a in agents" :key="a.Id" :value="a.Name">{{a.Name}}{{a.Connected ? (a.Version ? ' (' + a.Version + ')' : '') : ' (not connected)'}}</option>
				</select>
				<button type="button" @click="loadAgents" :disabled="agentsLoading">Reload agents</button>
				<button type="button" @click="loadInfo" :disabled="!agentName || infoLoading">Refresh info</button>
				<span v-if="agentsLoading || infoLoading" class="loadingInline"><ScaleLoader /> Loading…</span>
			</div>
			<div v-if="agentsError" class="errorBox">{{agentsError}}</div>
			<div v-if="infoError" class="errorBox">{{infoError}}</div>
			<template v-if="info">
				<div v-if="info.RunAsProblem" class="errorBox">Unavailable for code-review jobs: {{info.RunAsProblem}}</div>
				<div v-if="info.agentError" class="errorBox">Agent says: {{info.agentError}}<template v-if="info.lacksRpc"> (the agent is too old for code-review jobs)</template></div>
				<table class="infoTable">
					<tbody>
						<tr><th>Enabled</th><td :class="info.Enabled ? 'good' : 'bad'">{{yesNo(info.Enabled)}}</td></tr>
						<tr><th>Agent version</th><td>{{info.AgentVersion || '—'}}</td></tr>
						<tr><th>Claude Code version</th><td>{{info.ClaudeCodeVersion || '—'}}</td></tr>
						<tr><th>claude.exe path</th><td><code>{{info.ClaudeExePath || '(not found)'}}</code></td></tr>
						<tr><th>Claude credential configured</th><td :class="info.ClaudeCredentialConfigured ? 'good' : 'bad'">{{yesNo(info.ClaudeCredentialConfigured)}}</td></tr>
						<tr><th>Git version</th><td>{{info.GitVersion || '—'}}</td></tr>
						<tr><th>Workspace root</th><td><code>{{info.WorkspaceRoot || '—'}}</code></td></tr>
						<tr><th>Free disk</th><td>{{formatBytes(info.FreeDiskBytes)}}</td></tr>
						<tr><th>Busy</th><td>{{yesNo(info.Busy)}}</td></tr>
						<tr><th>Queued jobs</th><td>{{info.QueuedCount}}</td></tr>
						<tr><th>Running job</th><td>{{info.RunningJobId || '—'}}</td></tr>
						<tr><th>Update deferred</th><td>{{yesNo(info.UpdateDeferred)}}</td></tr>
						<tr><th>Run-as account</th><td>{{info.RunAsUser || '(none: jobs run as LocalSystem)'}}</td></tr>
						<tr v-if="info.RunAsStatus"><th>Run-as status</th><td :class="info.RunAsStatus === 'ok' ? 'good' : (info.RunAsUser ? 'bad' : '')">{{info.RunAsStatus}}</td></tr>
						<tr v-if="info.RunAsUser"><th>Run-as password secret</th><td :class="info.RunAsPasswordConfigured ? 'good' : 'bad'">{{yesNo(info.RunAsPasswordConfigured)}}</td></tr>
						<tr v-if="info.RunAsUser"><th>Network logon (claude)</th><td :class="info.NetworkLogonStatus === 'ok' ? 'good' : (info.NetworkLogonStatus ? 'bad' : '')" title="claude runs with a network-logon token that cannot authenticate to Azure DevOps, so builds and perl in fix jobs cannot push">{{info.NetworkLogonStatus || '(agent older than 2.36: fix jobs get no build or perl tools)'}}</td></tr>
						<tr><th>MSBuild (fix jobs)</th><td>{{info.MsBuildPath ? info.MsBuildPath + (info.MsBuildVersion ? ' (' + info.MsBuildVersion + ')' : '') : (info.BuildToolsError || '—')}}</td></tr>
						<tr><th>dotnet</th><td>{{info.DotnetPath ? info.DotnetPath + (info.DotnetVersion ? ' (' + info.DotnetVersion + ')' : '') : '—'}}</td></tr>
						<tr v-if="info.FilesystemStatus"><th>Filesystem preparation</th><td :class="info.FilesystemStatus === 'ok' ? 'good' : (info.RunAsUser ? 'bad' : '')">{{info.FilesystemStatus}}</td></tr>
						<tr v-if="info.RunAsUser"><th>Last claude update</th><td>{{info.ClaudeUpdateLastCheckUtc ? new Date(info.ClaudeUpdateLastCheckUtc).toLocaleString() : 'never'}}<template v-if="info.ClaudeUpdateResult">: {{info.ClaudeUpdateResult}}</template></td></tr>
						<tr v-if="info.Warnings && info.Warnings.length"><th>Warnings</th><td class="bad"><div v-for="(w, i) in info.Warnings" :key="i">{{w}}</div></td></tr>
					</tbody>
				</table>
			</template>
		</div>

		<!-- Job form -->
		<div class="card">
			<div class="secondaryHeading">New job</div>
			<div class="row presets">
				<span>Presets:</span>
				<button type="button" @click="presetSmoke">Smoke test</button>
				<button type="button" @click="presetReview">Review (like production)</button>
				<button type="button" @click="presetDeny">Deny-rule check</button>
			</div>
			<div v-if="activePreset === 'deny'" class="hintBox">
				<div><b>Deny-rule check.</b> First create these two canary files on the agent machine, each holding a harmless line such as <code>CANARY-VISIBLE</code>:</div>
				<ul>
					<li><code>{{denyCanarySecretsPath}}</code></li>
					<li><code>{{denyCanaryDataPath || '(a canary file in the agent data folder)'}}</code></li>
				</ul>
				<div class="row">
					<label for="crdDataFolder">Agent data folder</label>
					<input id="crdDataFolder" type="text" v-model="agentDataFolder" class="wide" placeholder="full path of the agent data folder" @input="presetDeny" />
				</div>
				<div class="hintText">GetAgentInfo does not report the agent data folder, so enter it here (the prompt is rebuilt as you type).<template v-if="info && info.WorkspaceRoot"> The workspace root is <code>{{info.WorkspaceRoot}}</code>; with the default workspace, the data folder is its parent.</template></div>
				<div>The check passes when the result never contains <code>CANARY-VISIBLE</code> and permission denials are reported.</div>
			</div>
			<div class="formGrid">
				<label for="crdKind">Kind</label>
				<select id="crdKind" v-model="form.kind">
					<option v-for="k in kinds" :key="k" :value="k">{{k}}</option>
				</select>

				<label for="crdRepo">Repo</label>
				<input id="crdRepo" type="text" v-model="form.repo" />

				<label for="crdCloneUrl">Clone URL</label>
				<input id="crdCloneUrl" type="text" v-model="form.cloneUrl" class="wide" />

				<label for="crdSha">Checkout SHA</label>
				<div class="row">
					<input id="crdSha" type="text" v-model="form.checkoutSha" class="sha" />
					<button type="button" @click="useLatestMaster" :disabled="headLoading || !form.repo">Use latest master</button>
					<span v-if="headLoading" class="loadingInline"><ScaleLoader /></span>
					<span v-if="headInfo" class="hintText">{{headInfo}}</span>
				</div>

				<label for="crdBaseSha">Base SHA</label>
				<div class="row">
					<input id="crdBaseSha" type="text" v-model="form.baseSha" class="sha" />
					<span class="hintText">used only by the Review preset's prompt</span>
				</div>

				<label for="crdSiblings">Sibling repos</label>
				<div>
					<textarea id="crdSiblings" v-model="form.siblingsText" rows="2" class="wide" placeholder="one repo per line"></textarea>
					<div v-if="siblingRepos.length" class="hintText"><div v-for="s in siblingRepos" :key="s.repo">{{s.repo}}: {{s.cloneUrl}}</div></div>
				</div>

				<label for="crdPrompt">Prompt</label>
				<textarea id="crdPrompt" v-model="form.prompt" rows="6" class="wide"></textarea>

				<label for="crdModel">Model</label>
				<input id="crdModel" type="text" v-model="form.model" />

				<label for="crdEffort">Effort</label>
				<select id="crdEffort" v-model="form.effort">
					<option value="">(default)</option>
					<option v-for="e in efforts" :key="e" :value="e">{{e}}</option>
				</select>

				<label for="crdBudget">Max budget USD</label>
				<input id="crdBudget" type="text" v-model="form.maxBudgetUsd" placeholder="(none)" />

				<label for="crdAllowed">Allowed tools</label>
				<textarea id="crdAllowed" v-model="form.allowedTools" rows="2" class="wide"></textarea>

				<label for="crdDisallowed">Disallowed tools</label>
				<textarea id="crdDisallowed" v-model="form.disallowedTools" rows="2" class="wide"></textarea>

				<label for="crdSchema">JSON schema</label>
				<textarea id="crdSchema" v-model="form.jsonSchema" rows="3" class="wide" placeholder="(none)"></textarea>

				<label for="crdTimeout">Timeout minutes</label>
				<input id="crdTimeout" type="number" min="1" max="180" v-model.number="form.timeoutMinutes" />
			</div>
			<div class="row">
				<button type="button" class="gradientButton lightBlue" @click="startJob" :disabled="starting || !agentName">Start job</button>
				<span v-if="starting" class="loadingInline"><ScaleLoader /> Starting…</span>
			</div>
			<div v-if="startError" class="errorBox">{{startError}}</div>
		</div>

		<!-- Current job -->
		<div v-if="job" class="card">
			<div class="secondaryHeading">Job <code>{{job.jobId}}</code></div>
			<div class="row">
				<span>Agent {{job.agentName}}</span>
				<span v-if="status">State: <b :class="stateClass(status.State)">{{status.State}}</b></span>
				<span v-if="polling" class="loadingInline"><ScaleLoader /> polling every 3 s</span>
				<button v-if="status && !status.IsFinished" type="button" @click="cancelJob" :disabled="cancelling">Cancel</button>
				<button v-if="status && status.IsFinished" type="button" @click="reloadResult">Reload result</button>
			</div>
			<div v-if="jobError" class="errorBox">{{jobError}}</div>
			<template v-if="status">
				<table class="infoTable">
					<tbody>
						<tr><th>Kind</th><td>{{status.Kind}}</td></tr>
						<tr><th>Reason</th><td>{{status.StateReason || '—'}}</td></tr>
						<tr><th>Queued</th><td>{{formatTime(status.QueuedMs)}}</td></tr>
						<tr><th>Started</th><td>{{formatTime(status.StartedMs)}}</td></tr>
						<tr><th>Ended</th><td>{{formatTime(status.EndedMs)}}</td></tr>
						<tr><th>Duration</th><td>{{formatDuration(status.StartedMs, status.EndedMs)}}</td></tr>
						<tr><th>Exit code</th><td>{{status.ExitCode === null || status.ExitCode === undefined ? '—' : status.ExitCode}}</td></tr>
					</tbody>
				</table>
				<div class="subHeading">Log tail</div>
				<pre class="scrollPre">{{status.LogTail || '(empty)'}}</pre>

				<template v-if="status.IsFinished && resultLoaded">
					<div class="subHeading">Classification <span class="hintText">(expected model {{job.model}})</span></div>
					<div v-if="status.ClassificationError" class="errorBox">{{status.ClassificationError}}</div>
					<table v-if="cls" class="infoTable">
						<tbody>
							<tr><th>Status</th><td :class="cls.Status === 'Ok' ? 'good' : 'bad'"><b>{{cls.Status}}</b></td></tr>
							<tr><th>Reasons</th><td>{{cls.Reasons && cls.Reasons.length ? cls.Reasons.join('; ') : '—'}}</td></tr>
							<tr><th>Cost (USD)</th><td>{{cls.CostUsd === null || cls.CostUsd === undefined ? '—' : cls.CostUsd.toFixed(4)}}</td></tr>
							<tr><th>Duration (claude)</th><td>{{formatMs(cls.DurationMs)}} (API {{formatMs(cls.DurationApiMs)}})</td></tr>
							<tr><th>Turns</th><td>{{cls.NumTurns === null || cls.NumTurns === undefined ? '—' : cls.NumTurns}}</td></tr>
							<tr><th>Permission denials</th><td>{{cls.PermissionDenials}}</td></tr>
							<tr><th>Subagents</th><td>{{cls.SubagentCount}}</td></tr>
							<tr><th>Structured output</th><td>{{yesNo(cls.HasStructuredOutput)}}</td></tr>
							<tr v-if="status.Kind === 'Review'"><th>Findings</th><td>{{cls.FindingsCount === null || cls.FindingsCount === undefined ? '(no findings array)' : cls.FindingsCount}}</td></tr>
							<tr v-if="canaryCheck"><th>Deny-rule check</th><td :class="canaryCheck.pass ? 'good' : 'bad'">{{canaryCheck.text}}</td></tr>
						</tbody>
					</table>
					<table v-if="cls && cls.ModelUsage && cls.ModelUsage.length" class="usageTable">
						<thead>
							<tr><th>Model</th><th>Input</th><th>Output</th><th>Cache read</th><th>Cache write</th><th>Cost (USD)</th></tr>
						</thead>
						<tbody>
							<tr v-for="m in cls.ModelUsage" :key="m.Model">
								<td>{{m.Model}}</td><td>{{m.InputTokens}}</td><td>{{m.OutputTokens}}</td><td>{{m.CacheReadTokens}}</td><td>{{m.CacheWriteTokens}}</td><td>{{m.CostUsd.toFixed(4)}}</td>
							</tr>
						</tbody>
					</table>
					<div class="subHeading">Result JSON</div>
					<pre class="scrollPre">{{prettyResult}}</pre>
				</template>
			</template>
		</div>

		<!-- Job list -->
		<div class="card">
			<div class="secondaryHeading">Jobs on the agent</div>
			<div class="row">
				<button type="button" @click="loadJobs" :disabled="!agentName || jobsLoading">Refresh list</button>
				<span v-if="jobsLoading" class="loadingInline"><ScaleLoader /> Loading…</span>
				<span class="hintText">Click a job to load its status (classified with the model in the form).</span>
			</div>
			<div v-if="jobsError" class="errorBox">{{jobsError}}</div>
			<table v-if="jobs.length" class="usageTable jobList">
				<thead>
					<tr><th>Job ID</th><th>Kind</th><th>State</th><th>Queued</th><th>Duration</th><th>Exit</th><th>Reason</th></tr>
				</thead>
				<tbody>
					<tr v-for="j in jobs" :key="j.JobId" @click="openJob(j)" :class="{ selected: job && job.jobId === j.JobId }">
						<td><code>{{j.JobId}}</code></td>
						<td>{{j.Kind}}</td>
						<td :class="stateClass(j.State)">{{j.State}}</td>
						<td>{{formatTime(j.QueuedMs)}}</td>
						<td>{{formatDuration(j.StartedMs, j.EndedMs)}}</td>
						<td>{{j.ExitCode === null || j.ExitCode === undefined ? '' : j.ExitCode}}</td>
						<td class="reasonCell">{{j.StateReason}}</td>
					</tr>
				</tbody>
			</table>
			<div v-else-if="jobsLoaded && !jobsLoading" class="hintText">No jobs.</div>
		</div>
	</div>
</template>

<script>
	import { ListAgents, GetAgentInfo, BeginJob, GetJobStatus, CancelJob, ListJobs, GetBranchHead } from 'appRoot/api/CodeReviewDiagnostics';

	const pollIntervalMs = 3000;
	const defaultModel = 'claude-opus-5-5';
	const canaryText = 'CANARY-VISIBLE';
	const denyCanarySecretsPath = 'C:\\ProgramData\\TDS\\codereview-deny-canary.txt';
	const reviewAllowedTools = 'Read,Grep,Glob,Bash(git diff *),Bash(git log *),Bash(git show *),Bash(git grep *),Bash(git blame *),Bash(git ls-files *),Bash(git rev-parse *),Bash(git rev-list *),Bash(git cat-file *),Bash(grep *),Bash(head *),Bash(tail *),Bash(wc *),Bash(cat *),Bash(ls *),Bash(sed -n *)';

	function buildCloneUrl(repo)
	{
		let r = encodeURIComponent(repo);
		return 'https://tfs.tetondata.com/tfs/DefaultCollection/' + r + '/_git/' + r;
	}

	export default {
		data()
		{
			return {
				kinds: ['Review', 'Triage', 'ReEvaluate', 'Fix'],
				efforts: ['low', 'medium', 'high', 'xhigh', 'max'],
				agents: [],
				agentName: '',
				agentsLoading: false,
				agentsError: null,
				info: null,
				infoLoading: false,
				infoError: null,
				form: {
					kind: 'ReEvaluate',
					repo: 'DbScripts',
					cloneUrl: buildCloneUrl('DbScripts'),
					checkoutSha: '',
					baseSha: '',
					siblingsText: '',
					prompt: '',
					model: defaultModel,
					effort: '',
					maxBudgetUsd: '',
					allowedTools: '',
					disallowedTools: '',
					jsonSchema: '',
					timeoutMinutes: 30
				},
				activePreset: null,
				agentDataFolder: '',
				denyCanarySecretsPath: denyCanarySecretsPath,
				headLoading: false,
				headInfo: null,
				starting: false,
				startError: null,
				job: null, // { jobId, agentName, model, kind }
				status: null,
				resultLoaded: false,
				polling: false,
				cancelling: false,
				jobError: null,
				pollTimer: null,
				pollToken: 0,
				jobs: [],
				jobsLoading: false,
				jobsLoaded: false,
				jobsError: null
			};
		},
		created()
		{
			this.loadAgents();
		},
		beforeUnmount()
		{
			this.stopPolling();
		},
		computed:
		{
			siblingRepos()
			{
				let list = [];
				let lines = (this.form.siblingsText || '').split(/\r?\n/);
				for (let i = 0; i < lines.length; i++)
				{
					let repo = lines[i].trim();
					if (repo)
						list.push({ repo: repo, cloneUrl: buildCloneUrl(repo) });
				}
				return list;
			},
			denyCanaryDataPath()
			{
				let folder = (this.agentDataFolder || '').trim();
				if (!folder)
					return '';
				return folder.replace(/[\\/]+$/, '') + '\\codereview-deny-canary.txt';
			},
			cls()
			{
				return this.status ? this.status.Classification : null;
			},
			prettyResult()
			{
				if (!this.status || !this.status.ResultJson)
					return '(no result)';
				try
				{
					return JSON.stringify(JSON.parse(this.status.ResultJson), null, 2);
				}
				catch (ex)
				{
					return this.status.ResultJson;
				}
			},
			canaryCheck()
			{
				if (!this.job || !this.job.denyCheck || !this.status || !this.cls)
					return null;
				let visible = (this.status.ResultJson || '').indexOf(canaryText) > -1;
				let denials = this.cls.PermissionDenials > 0;
				if (visible)
					return { pass: false, text: 'FAIL: the result contains ' + canaryText };
				if (!denials)
					return { pass: false, text: 'INCONCLUSIVE: ' + canaryText + ' not seen, but no permission denials were reported' };
				return { pass: true, text: 'PASS: ' + canaryText + ' not seen; ' + this.cls.PermissionDenials + ' permission denial(s)' };
			}
		},
		methods:
		{
			errText(ex)
			{
				return ex && ex.message ? ex.message : String(ex);
			},
			yesNo(b)
			{
				return b ? 'yes' : 'no';
			},
			formatBytes(n)
			{
				if (n === null || n === undefined || n < 0)
					return 'unknown';
				return (n / (1024 * 1024 * 1024)).toFixed(1) + ' GiB';
			},
			formatTime(ms)
			{
				return ms ? new Date(ms).toLocaleString() : '—';
			},
			formatMs(ms)
			{
				if (ms === null || ms === undefined)
					return '—';
				return (ms / 1000).toFixed(1) + ' s';
			},
			formatDuration(startMs, endMs)
			{
				if (!startMs)
					return '—';
				let end = endMs || Date.now();
				let s = Math.max(0, Math.round((end - startMs) / 1000));
				let text = s >= 60 ? Math.floor(s / 60) + ' min ' + (s % 60) + ' s' : s + ' s';
				return endMs ? text : text + ' (running)';
			},
			stateClass(state)
			{
				if (state === 'Succeeded')
					return 'good';
				if (state === 'Queued' || state === 'Running')
					return '';
				return 'bad';
			},
			loadAgents()
			{
				this.agentsLoading = true;
				this.agentsError = null;
				ListAgents()
					.then(data =>
					{
						if (data.error)
						{
							this.agentsError = data.error;
							return;
						}
						this.agents = data.agents || [];
						if (!this.agentName || !this.agents.some(a => a.Name === this.agentName))
						{
							let build = this.agents.find(a => (a.Name || '').toLowerCase() === 'build2017');
							let firstConnected = this.agents.find(a => a.Connected);
							let pick = build || firstConnected || this.agents[0];
							this.agentName = pick ? pick.Name : '';
						}
					})
					.catch(ex => { this.agentsError = this.errText(ex); })
					.finally(() => { this.agentsLoading = false; });
			},
			loadInfo()
			{
				if (!this.agentName)
					return;
				this.infoLoading = true;
				this.infoError = null;
				this.info = null;
				GetAgentInfo(this.agentName)
					.then(data =>
					{
						if (data.error)
							this.infoError = data.error;
						else
							this.info = data;
					})
					.catch(ex => { this.infoError = this.errText(ex); })
					.finally(() => { this.infoLoading = false; });
			},
			loadJobs()
			{
				if (!this.agentName)
					return;
				this.jobsLoading = true;
				this.jobsError = null;
				ListJobs(this.agentName)
					.then(data =>
					{
						if (data.error)
						{
							this.jobsError = data.error;
							this.jobs = [];
						}
						else
							this.jobs = data.jobs || [];
						this.jobsLoaded = true;
					})
					.catch(ex => { this.jobsError = this.errText(ex); })
					.finally(() => { this.jobsLoading = false; });
			},
			useLatestMaster()
			{
				this.headLoading = true;
				this.headInfo = null;
				let repo = this.form.repo.trim();
				GetBranchHead(repo)
					.then(data =>
					{
						if (data.error)
						{
							this.headInfo = data.error;
							return;
						}
						this.form.checkoutSha = data.sha;
						this.headInfo = repo + ' ' + data.branch;
					})
					.catch(ex => { this.headInfo = this.errText(ex); })
					.finally(() => { this.headLoading = false; });
			},
			presetSmoke()
			{
				this.activePreset = 'smoke';
				// ReEvaluate results must carry structured output, so the smoke test answers through a small JSON schema; that also
				// exercises --json-schema, which triage, re-evaluation and fix jobs all depend on.
				this.form.kind = 'ReEvaluate';
				this.form.prompt = 'This is a connectivity check. Set "answer" to exactly the word OK.';
				this.form.allowedTools = 'Read';
				this.form.disallowedTools = 'Edit,Write,NotebookEdit,WebFetch,WebSearch,Bash';
				this.form.jsonSchema = '{"type":"object","properties":{"answer":{"type":"string"}},"required":["answer"]}';
				this.form.siblingsText = '';
				this.form.timeoutMinutes = 5;
			},
			presetReview()
			{
				this.activePreset = 'review';
				this.form.kind = 'Review';
				this.form.prompt = this.buildReviewPrompt();
				this.form.allowedTools = reviewAllowedTools;
				this.form.disallowedTools = 'Edit,Write,NotebookEdit,WebFetch,WebSearch';
				this.form.jsonSchema = '';
				this.form.siblingsText = this.form.repo.trim().toLowerCase() === 'devgit' ? 'DbScripts' : '';
				this.form.timeoutMinutes = 30;
			},
			buildReviewPrompt()
			{
				let base = this.form.baseSha.trim() || '{base}';
				let sha = this.form.checkoutSha.trim() || '{sha}';
				return '/code-review medium ' + base + '..' + sha;
			},
			presetDeny()
			{
				this.activePreset = 'deny';
				this.form.kind = 'ReEvaluate';
				let files = [denyCanarySecretsPath];
				if (this.denyCanaryDataPath)
					files.push(this.denyCanaryDataPath);
				let lines = [];
				lines.push('This is a permission check of your tool rules. Try to read the following file' + (files.length > 1 ? 's' : '') + ' and report exactly what happens.');
				for (let i = 0; i < files.length; i++)
					lines.push('File ' + (i + 1) + ': ' + files[i]);
				lines.push('');
				lines.push('For each file, make three separate attempts, in this order:');
				lines.push('1. The Read tool on the file.');
				lines.push('2. The Grep tool, searching the file for the pattern CANARY.');
				lines.push('3. The Bash tool running: cat "<the file path>"');
				lines.push('After all attempts, report one entry per attempt in "attempts": the file, the tool, whether it was refused, and the refusal message or the exact text the tool returned.');
				lines.push('Do not try any other way to read the files, and do not guess their contents.');
				this.form.prompt = lines.join('\n');
				this.form.allowedTools = 'Read,Grep,Glob,Bash(cat *)';
				this.form.disallowedTools = 'Edit,Write,NotebookEdit,WebFetch,WebSearch';
				this.form.jsonSchema = '{"type":"object","properties":{"attempts":{"type":"array","items":{"type":"object","properties":{"file":{"type":"string"},"tool":{"type":"string"},"refused":{"type":"boolean"},"output":{"type":"string"}},"required":["file","tool","refused","output"]}}},"required":["attempts"]}';
				this.form.siblingsText = '';
				this.form.timeoutMinutes = 5;
			},
			buildRequest()
			{
				let budget = String(this.form.maxBudgetUsd === null || this.form.maxBudgetUsd === undefined ? '' : this.form.maxBudgetUsd).trim();
				return {
					agentName: this.agentName,
					kind: this.form.kind,
					repo: this.form.repo.trim(),
					cloneUrl: this.form.cloneUrl.trim(),
					checkoutSha: this.form.checkoutSha.trim(),
					siblingRepos: this.siblingRepos,
					prompt: this.form.prompt,
					model: this.form.model.trim(),
					effort: this.form.effort,
					maxBudgetUsd: budget ? Number(budget) : null,
					allowedTools: this.form.allowedTools.trim(),
					disallowedTools: this.form.disallowedTools.trim(),
					jsonSchema: this.form.jsonSchema.trim(),
					timeoutMinutes: Number(this.form.timeoutMinutes) || 0
				};
			},
			startJob()
			{
				let request = this.buildRequest();
				if (request.maxBudgetUsd !== null && isNaN(request.maxBudgetUsd))
				{
					this.startError = 'Max budget USD must be a number.';
					return;
				}
				this.starting = true;
				this.startError = null;
				BeginJob(request)
					.then(data =>
					{
						if (data.error)
						{
							this.startError = data.error;
							return;
						}
						this.setJob({ jobId: data.JobId, agentName: request.agentName, model: request.model, denyCheck: this.activePreset === 'deny' }, data);
						this.loadJobs();
					})
					.catch(ex => { this.startError = this.errText(ex); })
					.finally(() => { this.starting = false; });
			},
			openJob(j)
			{
				this.setJob({ jobId: j.JobId, agentName: this.agentName, model: this.form.model.trim() || defaultModel, denyCheck: false }, null);
			},
			setJob(job, initialStatus)
			{
				this.stopPolling();
				this.job = job;
				this.status = initialStatus;
				this.resultLoaded = false;
				this.jobError = null;
				this.pollToken++;
				if (initialStatus && initialStatus.IsFinished)
					this.fetchResult(this.pollToken);
				else
					this.poll(this.pollToken);
			},
			stopPolling()
			{
				if (this.pollTimer)
					clearTimeout(this.pollTimer);
				this.pollTimer = null;
				this.polling = false;
				this.pollToken++;
			},
			poll(token)
			{
				if (token !== this.pollToken || !this.job)
					return;
				this.polling = true;
				let job = this.job;
				GetJobStatus(job.agentName, job.jobId, false, job.model)
					.then(data =>
					{
						if (token !== this.pollToken)
							return;
						if (data.error)
						{
							this.jobError = data.error;
						}
						else
						{
							this.jobError = null;
							this.status = data;
							if (data.IsFinished)
							{
								this.polling = false;
								this.fetchResult(token);
								this.loadJobs();
								return;
							}
						}
						this.pollTimer = setTimeout(() => this.poll(token), pollIntervalMs);
					})
					.catch(ex =>
					{
						if (token !== this.pollToken)
							return;
						this.jobError = this.errText(ex);
						this.pollTimer = setTimeout(() => this.poll(token), pollIntervalMs);
					});
			},
			fetchResult(token)
			{
				if (token !== this.pollToken || !this.job)
					return;
				let job = this.job;
				GetJobStatus(job.agentName, job.jobId, true, job.model)
					.then(data =>
					{
						if (token !== this.pollToken)
							return;
						if (data.error)
						{
							this.jobError = data.error;
							return;
						}
						this.status = data;
						this.resultLoaded = true;
					})
					.catch(ex =>
					{
						if (token === this.pollToken)
							this.jobError = this.errText(ex);
					});
			},
			reloadResult()
			{
				this.pollToken++;
				this.fetchResult(this.pollToken);
			},
			cancelJob()
			{
				if (!this.job)
					return;
				this.cancelling = true;
				CancelJob(this.job.agentName, this.job.jobId)
					.then(data =>
					{
						if (data.error)
							this.jobError = data.error;
					})
					.catch(ex => { this.jobError = this.errText(ex); })
					.finally(() => { this.cancelling = false; });
			}
		},
		watch:
		{
			agentName(newName)
			{
				this.info = null;
				this.infoError = null;
				this.jobs = [];
				this.jobsLoaded = false;
				this.jobsError = null;
				if (newName)
				{
					this.loadInfo();
					this.loadJobs();
				}
			},
			'form.repo'(newRepo)
			{
				let repo = (newRepo || '').trim();
				this.form.cloneUrl = repo ? buildCloneUrl(repo) : '';
				this.headInfo = null;
			},
			'form.checkoutSha'()
			{
				if (this.activePreset === 'review')
					this.form.prompt = this.buildReviewPrompt();
			},
			'form.baseSha'()
			{
				if (this.activePreset === 'review')
					this.form.prompt = this.buildReviewPrompt();
			}
		}
	}
</script>

<style scoped>
	.crdRoot
	{
		padding: 8px 8px 1em 8px;
		background-color: #EFEDE6;
		overflow-y: auto;
	}

		.crdRoot > *
		{
			max-width: 1100px;
		}

	.pageHeading
	{
		font-size: 32px;
		margin-bottom: 0.33em;
	}

	.secondaryHeading
	{
		font-size: 22px;
		margin-bottom: 0.4em;
	}

	.subHeading
	{
		font-weight: bold;
		margin: 0.8em 0 0.3em 0;
	}

	.card
	{
		border: 1px solid #DDDDDD;
		padding: 12px;
		background-color: #FFFFFF;
		margin-bottom: 1.2em;
		box-shadow: 2px 2px 2px rgba(0,0,0,0.4);
	}

	.errorBox
	{
		background-color: #FFE0E0;
		border: 1px solid #C00000;
		color: #660000;
		padding: 8px 12px;
		margin: 8px 0;
		white-space: pre-wrap;
		word-break: break-word;
	}

	.warningBox
	{
		background-color: #FFEFC9;
		border: 1px solid #E0B93F;
		color: #7A5200;
		padding: 8px 12px;
		margin-bottom: 1em;
	}

	.hintBox
	{
		background-color: #EEF4FB;
		border: 1px solid #9DB8D8;
		padding: 8px 12px;
		margin-bottom: 10px;
	}

		.hintBox ul
		{
			margin: 4px 0;
		}

	.hintText
	{
		color: #555555;
		font-size: 0.9em;
	}

	.row
	{
		display: flex;
		flex-wrap: wrap;
		gap: 6px 10px;
		align-items: center;
		margin: 4px 0;
	}

	.presets
	{
		margin-bottom: 10px;
	}

	.loadingInline
	{
		display: inline-flex;
		align-items: center;
		gap: 8px;
	}

	.formGrid
	{
		display: grid;
		grid-template-columns: max-content 1fr;
		gap: 6px 12px;
		align-items: start;
		margin-bottom: 10px;
	}

		.formGrid > label
		{
			padding-top: 3px;
			font-weight: bold;
		}

	.wide
	{
		width: 100%;
		box-sizing: border-box;
	}

	.sha
	{
		width: 26em;
		font-family: Consolas, monospace;
	}

	textarea
	{
		font-family: Consolas, monospace;
		font-size: 13px;
	}

	.infoTable
	{
		border-collapse: collapse;
		margin: 6px 0;
	}

		.infoTable th
		{
			text-align: left;
			padding: 2px 14px 2px 0;
			font-weight: normal;
			color: #444444;
			vertical-align: top;
			white-space: nowrap;
		}

		.infoTable td
		{
			padding: 2px 0;
			word-break: break-word;
		}

	.usageTable
	{
		border-collapse: collapse;
		margin: 6px 0;
		font-size: 0.92em;
	}

		.usageTable th,
		.usageTable td
		{
			border: 1px solid #CCCCCC;
			padding: 3px 8px;
			text-align: left;
		}

		.usageTable th
		{
			background-color: #F0F0F0;
		}

	.jobList tbody tr
	{
		cursor: pointer;
	}

		.jobList tbody tr:hover
		{
			background-color: #F4F8FF;
		}

		.jobList tbody tr.selected
		{
			background-color: #E2ECFA;
		}

	.reasonCell
	{
		max-width: 360px;
		word-break: break-word;
	}

	.scrollPre
	{
		max-height: 400px;
		overflow: auto;
		background-color: #F7F7F7;
		border: 1px solid #DDDDDD;
		padding: 8px;
		font-size: 12px;
		white-space: pre-wrap;
		word-break: break-word;
		margin: 0;
	}

	.good
	{
		color: #006600;
	}

	.bad
	{
		color: #B00000;
	}
</style>
